/**
 * World entities that the robot can interact with: enemies and loot.
 *
 * `Enemy` is a discriminated union. Each concrete kind owns only the
 * fields its behavior needs. Shared fields (position, HP, status effects,
 * patrol wander state — every kind wanders when idle) live on `BaseEnemy`.
 *
 * Consumers that need kind-specific fields MUST narrow via the
 * `enemyKind` discriminator (`switch` or `if (e.enemyKind === 'charger')`).
 * Previously all fields were flat on `Enemy`, and any code could mutate
 * fields belonging to other behaviors — the union prevents that at the
 * type level.
 */
import type { Labyrinth } from './Labyrinth';
import type { StatusEffect } from './StatusEffect';
import { hasStatusEffect, getStatusEffect } from './StatusEffect';

export type EntityType = 'enemy' | 'loot';

export type EnemyKind = 'turret' | 'patrol' | 'charger' | 'bomber' | 'shielder';

export type ChargeState = 'idle' | 'charging' | 'recovering';

// ─── Base entity (loot + enemy share these) ────────────────

export interface Entity {
  type: EntityType;
  x: number;        // world-space position
  y: number;
  alive: boolean;    // false = defeated / collected
  /** Visibility alpha: 1 = fully visible (in LOS), 0 = invisible, fades over time */
  visAlpha: number;
  /** Radar ping countdown — seconds remaining until blip fades (0 = invisible on radar) */
  radarTimer: number;
}

// ─── Shared enemy fields (every kind) ──────────────────────

/**
 * Fields common to every enemy kind. All kinds can wander, take damage,
 * and carry status effects — so patrol/target/status state lives here.
 * Kind-specific state lives on the per-kind interfaces below.
 */
interface BaseEnemy extends Entity {
  type: 'enemy';
  hp: number;
  maxHp: number;
  damage: number;
  attackSpeed: number;
  attackRange: number;
  attackTimer: number;
  xp: number;
  moveSpeed: number;
  angle: number;
  patrolX: number;
  patrolY: number;
  patrolRadius: number;
  _patrolTimer: number;
  _targetX: number;
  _targetY: number;
  baseMoveSpeed: number;
  statusEffects: StatusEffect[];
  isBoss: boolean;
  bossPhase: number;
}

// ─── Per-kind variants ─────────────────────────────────────

export interface TurretEnemy extends BaseEnemy {
  enemyKind: 'turret';
}

export interface PatrolEnemy extends BaseEnemy {
  enemyKind: 'patrol';
}

export interface BomberEnemy extends BaseEnemy {
  enemyKind: 'bomber';
  /** Radius of the explosion when a lobbed bomb lands. */
  bombAoeRadius: number;
}

export interface ChargerEnemy extends BaseEnemy {
  enemyKind: 'charger';
  chargeState: ChargeState;
  chargeTimer: number;
  chargeTargetX: number;
  chargeTargetY: number;
  chargeSpeed: number;
}

export interface ShielderEnemy extends BaseEnemy {
  enemyKind: 'shielder';
  /** Tiles — allies within this radius receive the DR aura. */
  auraRadius: number;
  /** Fraction of damage reduced for protected allies (0–1). */
  shieldReduction: number;
}

/** Discriminated union over every enemy kind. */
export type Enemy = TurretEnemy | PatrolEnemy | BomberEnemy | ChargerEnemy | ShielderEnemy;

// ─── Type guards (useful for callers that don't switch) ───

export function isCharger(e: Enemy): e is ChargerEnemy {
  return e.enemyKind === 'charger';
}
export function isBomber(e: Enemy): e is BomberEnemy {
  return e.enemyKind === 'bomber';
}
export function isShielder(e: Enemy): e is ShielderEnemy {
  return e.enemyKind === 'shielder';
}

// ─── Loot ──────────────────────────────────────────────────

export interface LootChest extends Entity {
  type: 'loot';
  lootTime: number;  // seconds to loot
  value: number;     // score / resource value
}

// ─── Factories ─────────────────────────────────────────────

/** Exponential tier multiplier — tier 1 = 1×, tier 5 ≈ 18× */
function tierScale(tier: number): number {
  return Math.pow(tier, 1.8);
}

/**
 * Build the shared base-enemy portion of a fresh enemy. Each kind's
 * factory then spreads this and adds its own fields.
 */
function baseEnemyFields(x: number, y: number): Omit<BaseEnemy,
  'enemyKind' | 'hp' | 'maxHp' | 'damage' | 'attackSpeed' | 'attackRange' |
  'xp' | 'moveSpeed' | 'baseMoveSpeed' | 'patrolRadius'
> {
  return {
    type: 'enemy',
    x, y,
    alive: true,
    visAlpha: 0,
    radarTimer: 0,
    attackTimer: 0,
    angle: Math.random() * Math.PI * 2,
    patrolX: x, patrolY: y,
    _patrolTimer: 0,
    _targetX: x, _targetY: y,
    statusEffects: [],
    isBoss: false,
    bossPhase: 0,
  };
}

/** Create a stationary turret enemy */
export function createEnemy(x: number, y: number, tier: number = 1): TurretEnemy {
  const s = tierScale(tier);
  const hp = Math.round(8 + 4 * s);
  return {
    ...baseEnemyFields(x, y),
    enemyKind: 'turret',
    hp,
    maxHp: hp,
    damage: 1 + 0.5 * s,
    attackSpeed: Math.max(0.4, 1.5 - tier * 0.15),
    attackRange: 10,
    xp: Math.round(5 + 3 * s),
    moveSpeed: 0,
    baseMoveSpeed: 0,
    patrolRadius: 0,
  };
}

/** Create a mobile patrol enemy — moves between random walkable tiles */
export function createPatrolEnemy(x: number, y: number, tier: number = 1): PatrolEnemy {
  const s = tierScale(tier);
  const hp = Math.round(6 + 3 * s);
  const spd = 1.2 + tier * 0.2;
  return {
    ...baseEnemyFields(x, y),
    enemyKind: 'patrol',
    hp,
    maxHp: hp,
    damage: 1.5 + 0.6 * s,
    attackSpeed: Math.max(0.4, 1.3 - tier * 0.12),
    attackRange: 4,
    xp: Math.round(8 + 4 * s),
    moveSpeed: spd,
    baseMoveSpeed: spd,
    patrolRadius: 4 + tier,
    _patrolTimer: Math.random() * 3,
  };
}

/** Create a fast melee charger enemy — charges at robot on detection */
export function createChargerEnemy(x: number, y: number, tier: number = 1): ChargerEnemy {
  const s = tierScale(tier);
  const hp = Math.round(5 + 2 * s);
  const chargeSpd = 3.0 + tier * 0.4;
  return {
    ...baseEnemyFields(x, y),
    enemyKind: 'charger',
    hp,
    maxHp: hp,
    damage: 2 + 1.0 * s,
    attackSpeed: Math.max(0.8, 2.0 - tier * 0.15),
    attackRange: 1.0,
    xp: Math.round(10 + 5 * s),
    moveSpeed: 0.5,
    baseMoveSpeed: 0.5,
    patrolRadius: 3,
    _patrolTimer: Math.random() * 3,
    chargeState: 'idle',
    chargeTimer: 0,
    chargeTargetX: 0,
    chargeTargetY: 0,
    chargeSpeed: chargeSpd,
  };
}

/** Create an AoE bomber enemy — lobs arcing bombs at long range */
export function createBomberEnemy(x: number, y: number, tier: number = 1): BomberEnemy {
  const s = tierScale(tier);
  const hp = Math.round(7 + 3 * s);
  return {
    ...baseEnemyFields(x, y),
    enemyKind: 'bomber',
    hp,
    maxHp: hp,
    damage: 1.5 + 0.4 * s,
    attackSpeed: Math.max(0.8, 3.0 - tier * 0.3),
    attackRange: 7 + tier,
    attackTimer: Math.random() * 2, // stagger initial fire
    xp: Math.round(12 + 5 * s),
    moveSpeed: 0.4,
    baseMoveSpeed: 0.4,
    patrolRadius: 2,
    _patrolTimer: Math.random() * 3,
    bombAoeRadius: 1.5 + tier * 0.25,
  };
}

/** Create a shielder support enemy — provides damage reduction aura, does not attack */
export function createShielderEnemy(x: number, y: number, tier: number = 1): ShielderEnemy {
  const s = tierScale(tier);
  const hp = Math.round(10 + 5 * s);
  return {
    ...baseEnemyFields(x, y),
    enemyKind: 'shielder',
    hp,
    maxHp: hp,
    damage: 0,
    attackSpeed: 99,
    attackRange: 0,
    xp: Math.round(15 + 6 * s),
    moveSpeed: 0.8,
    baseMoveSpeed: 0.8,
    patrolRadius: 4,
    _patrolTimer: Math.random() * 3,
    auraRadius: 4 + tier * 0.5,
    shieldReduction: Math.min(0.6, 0.3 + tier * 0.07),
  };
}

/** Create a boss enemy — enhanced version of a given kind with massively scaled stats */
export function createBossEnemy(kind: EnemyKind, x: number, y: number, tier: number = 1): Enemy {
  let base: Enemy;
  switch (kind) {
    case 'turret':   base = createEnemy(x, y, tier); break;
    case 'charger':  base = createChargerEnemy(x, y, tier); break;
    case 'shielder': base = createShielderEnemy(x, y, tier); break;
    case 'patrol':   base = createPatrolEnemy(x, y, tier); break;
    case 'bomber':   base = createBomberEnemy(x, y, tier); break;
  }
  base.isBoss = true;
  base.hp *= 5;
  base.maxHp *= 5;
  base.damage *= 1.5;
  base.xp *= 8;
  return base;
}

// ─── Update functions ──────────────────────────────────────

/** Update patrol-style wander movement. Shared by turret-idle, patrol, bomber, charger-idle, shielder-without-allies. */
export function updatePatrolEnemy(enemy: Enemy, dt: number, labyrinth: Labyrinth) {
  if (!enemy.alive) return;
  if (hasStatusEffect(enemy, 'stun')) return;

  const slow = getStatusEffect(enemy, 'slow');
  enemy.moveSpeed = slow
    ? enemy.baseMoveSpeed * (1 - slow.magnitude)
    : enemy.baseMoveSpeed;

  if (enemy.moveSpeed <= 0) return;

  enemy._patrolTimer -= dt;

  const dxT = enemy._targetX - enemy.x;
  const dyT = enemy._targetY - enemy.y;
  const distToTarget = Math.hypot(dxT, dyT);

  if (enemy._patrolTimer <= 0 || distToTarget < 0.3) {
    for (let attempt = 0; attempt < 10; attempt++) {
      const angle = Math.random() * Math.PI * 2;
      const dist = Math.random() * enemy.patrolRadius;
      const nx = enemy.patrolX + Math.cos(angle) * dist;
      const ny = enemy.patrolY + Math.sin(angle) * dist;
      const tx = Math.floor(nx);
      const ty = Math.floor(ny);
      if (labyrinth.isWalkable(tx, ty)) {
        enemy._targetX = nx;
        enemy._targetY = ny;
        break;
      }
    }
    enemy._patrolTimer = 2 + Math.random() * 3;
  }

  if (distToTarget > 0.1) {
    const moveAngle = Math.atan2(dyT, dxT);
    const step = Math.min(enemy.moveSpeed * dt, distToTarget);
    const newX = enemy.x + Math.cos(moveAngle) * step;
    const newY = enemy.y + Math.sin(moveAngle) * step;

    if (labyrinth.isWalkable(Math.floor(newX), Math.floor(newY))) {
      enemy.x = newX;
      enemy.y = newY;
    } else {
      enemy._patrolTimer = 0;
    }
    enemy.angle = moveAngle;
  }
}

/** Update charger enemy AI. Handles idle wander, charge toward robot, and recovery. */
export function updateChargerEnemy(
  enemy: ChargerEnemy, dt: number, labyrinth: Labyrinth,
  robotX: number, robotY: number,
) {
  if (!enemy.alive) return;
  if (hasStatusEffect(enemy, 'stun')) {
    enemy.chargeState = 'recovering';
    enemy.chargeTimer = 0.5;
    return;
  }

  const slow = getStatusEffect(enemy, 'slow');
  enemy.moveSpeed = slow
    ? enemy.baseMoveSpeed * (1 - slow.magnitude)
    : enemy.baseMoveSpeed;

  const distToRobot = Math.hypot(robotX - enemy.x, robotY - enemy.y);

  switch (enemy.chargeState) {
    case 'idle': {
      updatePatrolEnemy(enemy, dt, labyrinth);

      const detectRange = enemy.isBoss ? 8 : 6;
      if (distToRobot <= detectRange) {
        enemy.chargeState = 'charging';
        enemy.chargeTargetX = robotX;
        enemy.chargeTargetY = robotY;
        enemy.chargeTimer = 0;
        enemy.angle = Math.atan2(robotY - enemy.y, robotX - enemy.x);
        if (enemy.isBoss) enemy.bossPhase++;
      }
      break;
    }

    case 'charging': {
      const rageMult = (enemy.isBoss && enemy.bossPhase >= 3) ? 1.5 : 1.0;
      const speed = (slow
        ? enemy.chargeSpeed * (1 - slow.magnitude)
        : enemy.chargeSpeed) * rageMult;
      const angle = Math.atan2(enemy.chargeTargetY - enemy.y, enemy.chargeTargetX - enemy.x);
      enemy.angle = angle;
      const step = speed * dt;
      const newX = enemy.x + Math.cos(angle) * step;
      const newY = enemy.y + Math.sin(angle) * step;

      if (!labyrinth.isWalkable(Math.floor(newX), Math.floor(newY))) {
        enemy.chargeState = 'recovering';
        enemy.chargeTimer = enemy.isBoss ? 0.6 : 1.0;
        break;
      }

      enemy.x = newX;
      enemy.y = newY;
      enemy.chargeTimer += dt;

      if (enemy.chargeTimer > 1.5) {
        enemy.chargeState = 'recovering';
        const recoveryTime = (enemy.isBoss && enemy.bossPhase >= 3) ? 0.5 : 1.0;
        enemy.chargeTimer = recoveryTime;
      }
      break;
    }

    case 'recovering': {
      enemy.chargeTimer -= dt;
      if (enemy.chargeTimer <= 0) {
        enemy.chargeState = 'idle';
        enemy.chargeTimer = 0;
      }
      break;
    }
  }
}

/** Update bomber enemy — slow drift, attack logic handled in Robot.ts */
export function updateBomberEnemy(enemy: BomberEnemy, dt: number, labyrinth: Labyrinth) {
  if (!enemy.alive) return;
  if (hasStatusEffect(enemy, 'stun')) return;
  updatePatrolEnemy(enemy, dt, labyrinth);
}

/** Update shielder enemy — moves toward clusters of nearby allies */
export function updateShielderEnemy(
  enemy: ShielderEnemy, dt: number, labyrinth: Labyrinth,
  allies: readonly Enemy[],
) {
  if (!enemy.alive) return;
  if (hasStatusEffect(enemy, 'stun')) return;

  const slow = getStatusEffect(enemy, 'slow');
  enemy.moveSpeed = slow
    ? enemy.baseMoveSpeed * (1 - slow.magnitude)
    : enemy.baseMoveSpeed;

  if (enemy.moveSpeed <= 0) return;

  let cx = 0, cy = 0, count = 0;
  const scanRange = enemy.auraRadius * 2;
  for (const ally of allies) {
    if (!ally.alive || ally === enemy) continue;
    if (ally.enemyKind === 'shielder') continue;
    const d = Math.hypot(ally.x - enemy.x, ally.y - enemy.y);
    if (d <= scanRange) {
      cx += ally.x;
      cy += ally.y;
      count++;
    }
  }

  if (count > 0) {
    cx /= count;
    cy /= count;
    const dx = cx - enemy.x;
    const dy = cy - enemy.y;
    const dist = Math.hypot(dx, dy);

    if (dist > 1.5) {
      const moveAngle = Math.atan2(dy, dx);
      const step = Math.min(enemy.moveSpeed * dt, dist);
      const newX = enemy.x + Math.cos(moveAngle) * step;
      const newY = enemy.y + Math.sin(moveAngle) * step;

      if (labyrinth.isWalkable(Math.floor(newX), Math.floor(newY))) {
        enemy.x = newX;
        enemy.y = newY;
      }
      enemy.angle = moveAngle;
    }
  } else {
    updatePatrolEnemy(enemy, dt, labyrinth);
  }
}

/** Check if an enemy is shielded by a nearby Shielder. Returns damage multiplier (1.0 = no shield).
 *  Accepts a pre-filtered list of alive shielders for O(shielders) instead of O(allEnemies). */
export function getShieldMultiplier(enemy: Enemy, shielders: readonly ShielderEnemy[]): number {
  let bestReduction = 0;
  for (const e of shielders) {
    if (e === enemy) continue;
    const d = Math.hypot(e.x - enemy.x, e.y - enemy.y);
    if (d <= e.auraRadius && e.shieldReduction > bestReduction) {
      bestReduction = e.shieldReduction;
    }
  }
  return 1 - bestReduction;
}

export function createLootChest(x: number, y: number, tier: number = 1): LootChest {
  return {
    type: 'loot',
    x, y,
    alive: true,
    // Loot is static — visibility is governed by the fog-of-war layer, not by
    // enemy-style LOS fade (which only runs for type === 'enemy').
    visAlpha: 1,
    radarTimer: 0,
    lootTime: 1.5 + tier * 0.5,
    value: 10 + tier * 5,
  };
}
